import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useBranch } from '../contexts/BranchContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { CreditCard, Key, Lightning, CheckCircle, XCircle, ArrowLeft, Coins } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const BranchBillingSettings = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeBranch } = useBranch();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use activeBranch if no branchId in URL (for owner accessing via sidebar)
  const effectiveBranchId = branchId || activeBranch?.branch_id;
  const [billingMethod, setBillingMethod] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [validatingKey, setValidatingKey] = useState(false);
  const [creditsData, setCreditsData] = useState(null);
  const [purchasingCredits, setPurchasingCredits] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Admin-only state for configuring billing method
  const [adminConfigMode, setAdminConfigMode] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [savingMethod, setSavingMethod] = useState(false);
  const [billingConfig, setBillingConfig] = useState(null);
  const [savingKey, setSavingKey] = useState(false);
  
  const isAdmin = user?.role === 'admin';

  // Flexible credit pricing — rate is admin-editable (stored server-side)
  const [creditRate, setCreditRate] = useState(1.5);
  const CREDIT_PRICE_PER_UNIT = creditRate;
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [customCredits, setCustomCredits] = useState('');

  const fetchCreditRate = async () => {
    try {
      const res = await api.get('/billing/credits/rate');
      setCreditRate(res.data.price_per_credit);
    } catch (e) { /* keep default */ }
  };
  useEffect(() => { fetchCreditRate(); }, []);

  const saveCreditRate = async () => {
    const rupees = parseFloat(rateInput);
    if (!rupees || rupees <= 0) { toast.error('Enter a valid rate'); return; }
    try {
      const res = await api.put('/billing/credits/rate', { price_rupees: rupees });
      setCreditRate(res.data.price_per_credit);
      setEditingRate(false);
      toast.success(`Per-credit rate updated to ₹${res.data.price_per_credit}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update rate'));
    }
  };
  
  // Quick-select credit amounts
  const QUICK_SELECT_CREDITS = [100, 500, 1000, 2000];

  useEffect(() => {
    if (effectiveBranchId) {
      fetchBranchDetails();
      fetchBillingConfig();
      fetchCreditsData();
      fetchTransactionHistory();
    }
  }, [effectiveBranchId]);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchBranchDetails = async () => {
    if (!effectiveBranchId) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get(`/branches/${effectiveBranchId}`);
      setBranch(response.data);
      setBillingMethod(response.data.ai_billing_method || null);
      setApiKey(response.data.ai_api_key || '');
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load branch details');
      setLoading(false);
    }
  };

  const fetchBillingConfig = async () => {
    if (!effectiveBranchId) return;
    try {
      const response = await api.get(`/branches/${effectiveBranchId}/billing`);
      setBillingConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch billing config:', error);
    }
  };

  const fetchCreditsData = async () => {
    if (!effectiveBranchId) return;
    try {
      const response = await api.get(`/billing/credits/${effectiveBranchId}`);
      setCreditsData(response.data);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const fetchTransactionHistory = async () => {
    if (!effectiveBranchId) return;
    setLoadingTransactions(true);
    try {
      const response = await api.get(`/billing/transactions/${effectiveBranchId}`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleSaveBillingMethod = async (method) => {
    if (!effectiveBranchId) return;
    setSavingMethod(true);
    try {
      await api.put(`/branches/${effectiveBranchId}`, {
        ai_billing_method: method
      });
      setBillingMethod(method);
      setAdminConfigMode(false);
      toast.success(`AI Billing method configured: ${method === 'own_key' ? 'Own API Key' : method === 'credits' ? 'Credits' : 'Disabled'}`);
      fetchBranchDetails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update billing method'));
    } finally {
      setSavingMethod(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!effectiveBranchId || !apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setSavingKey(true);
    try {
      await api.post(`/branches/${effectiveBranchId}/api-key`, {
        ai_api_key: apiKey
      });
      toast.success('API key saved successfully!');
      setApiKey('');
      fetchBillingConfig();
      fetchBranchDetails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save API key'));
    } finally {
      setSavingKey(false);
    }
  };

  const handlePurchaseCredits = async (credits) => {
    if (!effectiveBranchId) return;
    if (!credits || credits < 1) {
      toast.error('Please enter a valid credit amount');
      return;
    }
    
    setPurchasingCredits(true);
    try {
      // Create Razorpay order for flexible credits
      const orderResponse = await api.post('/billing/credits/create-order', {
        branch_id: effectiveBranchId,
        credits: parseInt(credits)
      });

      const { order_id, amount, currency, price_per_credit } = orderResponse.data;
      
      console.log('Order Created:', { order_id, amount, currency, credits });

      // Initialize Razorpay checkout with live key
      const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_key_missing';
      console.log('Razorpay Key ID:', razorpayKeyId.substring(0, 15) + '...');
      console.log('Amount in paise:', amount);
      
      const options = {
        key: razorpayKeyId,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'CERACARE AI Credits',
        description: `${credits} credits at ₹${price_per_credit}/credit`,
        image: 'https://i.imgur.com/n5tjHFD.png',
        handler: async (response) => {
          // Verify payment
          try {
            const verifyResponse = await api.post('/billing/credits/verify-payment', {
              branch_id: effectiveBranchId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              credits: parseInt(credits)
            });

            toast.success(verifyResponse.data.message);
            fetchCreditsData();
            fetchBranchDetails();
            fetchTransactionHistory();
            setCustomCredits(''); // Clear input after successful purchase
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name || branch?.name || '',
          email: user?.email || branch?.email || '',
          contact: branch?.phone || '+919876543210'
        },
        notes: {
          branch_id: effectiveBranchId,
          credits: credits
        },
        theme: {
          color: '#2C4C3B'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal closed by user');
            setPurchasingCredits(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', (response) => {
        console.error('Razorpay Payment Failed:', response.error);
        const errorMsg = response.error?.description || response.error?.reason || 'Payment failed';
        toast.error(`Payment Failed: ${errorMsg}`);
        setPurchasingCredits(false);
      });
      
      razorpay.open();

    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to initiate payment'));
      setPurchasingCredits(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/branch-management')} className="w-fit">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-3xl font-light text-[#2C4C3B]">AI Billing Configuration</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">{branch?.name}</p>
          </div>
        </div>

        {/* Current Status */}
        <Card className="p-4 sm:p-6 bg-gradient-to-r from-[#2C4C3B]/5 to-[#2C4C3B]/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Current AI Access</div>
              <div className="flex items-center gap-2 flex-wrap">
                {!billingMethod && (
                  <>
                    <XCircle size={20} className="text-red-500 sm:w-6 sm:h-6" weight="fill" />
                    <span className="text-base sm:text-lg font-semibold text-red-600">Not Configured</span>
                  </>
                )}
                {billingMethod === 'own_key' && (
                  <>
                    <CheckCircle size={20} className="text-green-500 sm:w-6 sm:h-6" weight="fill" />
                    <span className="text-base sm:text-lg font-semibold text-green-600">Own API Key</span>
                  </>
                )}
                {billingMethod === 'credits' && (
                  <>
                    <CheckCircle size={20} className="text-blue-500 sm:w-6 sm:h-6" weight="fill" />
                    <span className="text-base sm:text-lg font-semibold text-blue-600">Credits</span>
                    <Badge variant="outline" className="ml-2 text-xs">
                      {creditsData?.credits_balance || 0} credits
                    </Badge>
                  </>
                )}
              </div>
            </div>
            {isAdmin && billingMethod && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleSaveBillingMethod(null)}
                data-testid="disable-ai-access-button"
                className="w-full sm:w-auto text-sm"
              >
                Disable AI Access
              </Button>
            )}
            {isAdmin && !billingMethod && !adminConfigMode && (
              <Button 
                variant="default" 
                size="sm"
                onClick={() => setAdminConfigMode(true)}
                className="bg-[#2C4C3B] w-full sm:w-auto text-sm"
                data-testid="configure-billing-button"
              >
                Configure AI Billing
              </Button>
            )}
          </div>
        </Card>

        {/* Admin Configuration Form (shown when billing not configured) */}
        {isAdmin && !billingMethod && adminConfigMode && (
          <Card className="p-4 sm:p-6 border-2 border-[#2C4C3B]">
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-[#2C4C3B] mb-2">Configure AI Billing Method</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Select how this branch will access AI features. Once configured, the branch owner will only see this method.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <label className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" 
                     style={{borderColor: selectedMethod === 'own_key' ? '#2C4C3B' : '#e5e7eb'}}>
                <input
                  type="radio"
                  name="billing_method_config"
                  value="own_key"
                  checked={selectedMethod === 'own_key'}
                  onChange={() => setSelectedMethod('own_key')}
                  className="mt-1 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm sm:text-base text-[#2C4C3B]">Solution 1: Own API Key</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">
                    Branch owner provides their own Emergent LLM API key. Direct billing to their account.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                     style={{borderColor: selectedMethod === 'credits' ? '#2C4C3B' : '#e5e7eb'}}>
                <input
                  type="radio"
                  name="billing_method_config"
                  value="credits"
                  checked={selectedMethod === 'credits'}
                  onChange={() => setSelectedMethod('credits')}
                  className="mt-1 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm sm:text-base text-[#2C4C3B]">Solution 2: Subscription Credits (Razorpay)</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">
                    Owner purchases flexible credits via Razorpay. AI Credits: ₹1.50/credit, Notification Credits: ₹15/credit.
                  </div>
                </div>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setAdminConfigMode(false)}
                variant="outline"
                disabled={savingMethod}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSaveBillingMethod(selectedMethod)}
                disabled={!selectedMethod || savingMethod}
                className="bg-[#2C4C3B] w-full sm:w-auto"
                data-testid="save-billing-method-button"
              >
                {savingMethod ? 'Saving...' : 'Save Configuration'}
              </Button>
            </div>
          </Card>
        )}

        {/* API Key Management - shown when Solution 1 is selected and saved */}
        {!adminConfigMode && billingMethod === 'own_key' && (
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B] mb-1">Branch API Key</h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-4">Enter the Emergent LLM API key for this branch's AI features</p>
            
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-start">
              <div className="flex-1">
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter Emergent LLM API key..."
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent text-sm"
                />
              </div>
              <button
                onClick={handleSaveApiKey}
                disabled={!apiKey.trim() || savingKey}
                className="px-4 sm:px-5 py-2.5 bg-[#2C4C3B] text-white rounded-lg text-sm font-medium hover:bg-[#1a3a2a] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {savingKey ? 'Saving...' : 'Save Key'}
              </button>
            </div>
            
            <div className="mt-3 flex items-start gap-2 text-xs sm:text-sm">
              {billingConfig?.api_key_configured ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-green-600">✅</span>
                  <span className="text-green-700 font-medium">API Key configured</span>
                  {billingConfig?.api_key_preview && (
                    <span className="text-gray-400 text-xs">Key: {billingConfig.api_key_preview}</span>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-amber-500 flex-shrink-0">⚠️</span>
                  <span className="text-amber-700 font-medium">API Key not set yet — AI features will not work until a key is provided</span>
                </div>
              )}
            </div>
          </div>
        )}

        {billingMethod === 'credits' && (
          /* Show ONLY Credits/Razorpay option */
          <Card className="p-4 sm:p-6">
            <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Coins size={24} className="text-[#2C4C3B] mt-1 sm:w-8 sm:h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-semibold text-[#2C4C3B] mb-1 sm:mb-2">Credits Method (Razorpay)</h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  This branch uses pay-as-you-go credits. Each AI generation costs 1 credit (₹{CREDIT_PRICE_PER_UNIT}).
                </p>
              </div>
            </div>

            {/* Current Balance */}
            <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600">Current Balance</div>
                  <div className="text-3xl font-bold text-[#2C4C3B]">
                    {creditsData?.credits_balance || 0}
                    <span className="text-lg font-normal text-gray-600 ml-2">credits</span>
                  </div>
                </div>
                <Lightning size={48} className="text-yellow-500" weight="fill" />
              </div>
            </Card>

            {/* Credit Purchase - NEW FLEXIBLE INPUT */}
            <div className="space-y-4">
              <h4 className="font-semibold text-[#2C4C3B] mb-3">Purchase AI Credits</h4>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <p className="text-sm text-gray-600">
                  Buy credits at <span className="font-semibold text-[#2C4C3B]" data-testid="credit-rate-display">₹{CREDIT_PRICE_PER_UNIT}</span> per credit. Enter any amount you need.
                </p>
                {isAdmin && !editingRate && (
                  <button
                    type="button"
                    data-testid="edit-credit-rate-btn"
                    onClick={() => { setRateInput(String(CREDIT_PRICE_PER_UNIT)); setEditingRate(true); }}
                    className="text-xs font-medium text-[#C05C42] border border-[#C05C42]/40 rounded px-2 py-1 hover:bg-[#C05C42] hover:text-white transition-colors"
                  >
                    Edit rate
                  </button>
                )}
                {isAdmin && editingRate && (
                  <div className="flex items-center gap-2" data-testid="credit-rate-editor">
                    <span className="text-sm font-semibold text-[#2C4C3B]">₹</span>
                    <Input type="number" min="0.01" step="0.01" value={rateInput} onChange={(e) => setRateInput(e.target.value)}
                      data-testid="credit-rate-input" className="w-28 h-8" />
                    <span className="text-xs text-gray-500">per credit</span>
                    <Button size="sm" data-testid="credit-rate-save" onClick={saveCreditRate} className="bg-[#2C4C3B] text-white h-8">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingRate(false)} className="h-8">Cancel</Button>
                  </div>
                )}
              </div>

              {/* Custom Amount Input */}
              <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <Label htmlFor="custom-credits" className="text-base font-medium mb-2">
                  Enter Credit Amount
                </Label>
                <div className="flex gap-3 mt-2">
                  <div className="flex-1">
                    <Input
                      id="custom-credits"
                      type="number"
                      min="1"
                      max="100000"
                      value={customCredits}
                      onChange={(e) => setCustomCredits(e.target.value)}
                      placeholder="e.g., 500"
                      className="text-lg"
                    />
                    {customCredits && (
                      <p className="text-sm text-gray-600 mt-2">
                        Total: ₹{(parseFloat(customCredits) * CREDIT_PRICE_PER_UNIT).toFixed(2)}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={() => handlePurchaseCredits(customCredits)}
                    disabled={purchasingCredits || !customCredits || parseFloat(customCredits) < 1}
                    className="bg-[#2C4C3B] hover:bg-[#1f3529] px-8"
                  >
                    {purchasingCredits ? 'Processing...' : 'Buy Credits'}
                  </Button>
                </div>
              </div>

              {/* Quick Select Buttons */}
              <div>
                <p className="text-sm text-gray-600 mb-3">Quick Select:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {QUICK_SELECT_CREDITS.map((amount) => (
                    <Card 
                      key={amount}
                      onClick={() => setCustomCredits(amount.toString())}
                      className="p-4 hover:shadow-lg transition-all cursor-pointer border-2 hover:border-[#2C4C3B] text-center"
                    >
                      <div className="text-xl font-bold text-[#2C4C3B]">{amount}</div>
                      <div className="text-sm text-gray-600">credits</div>
                      <div className="text-xs text-gray-500 mt-1">
                        ₹{(amount * CREDIT_PRICE_PER_UNIT).toFixed(0)}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Pricing Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h5 className="font-medium text-blue-900 mb-2">Pricing Information</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Fixed rate: ₹{CREDIT_PRICE_PER_UNIT} per credit</li>
                  <li>• No minimum or maximum limits</li>
                  <li>• Credits never expire</li>
                  <li>• Each AI generation costs 1 credit</li>
                </ul>
              </div>
            </div>
          </Card>
        )}

        {!billingMethod && (
          /* Show message if not configured */
          <Card className="p-8 text-center">
            <Lightning size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">AI Billing Not Configured</h3>
            <p className="text-gray-500 mb-4">
              Your administrator has not set up AI billing for this branch yet.
            </p>
            <p className="text-sm text-gray-400">
              Please contact your system administrator to configure AI features access.
            </p>
          </Card>
        )}

        {/* Old Tabs UI - REMOVED, now showing conditional single-method UI above */}
        <div className="hidden">
        <Tabs defaultValue={billingMethod || 'choose'} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="own_key" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
              <Key className="w-4 h-4 mr-2" />
              Own API Key
            </TabsTrigger>
            <TabsTrigger value="credits" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Purchase Credits
            </TabsTrigger>
          </TabsList>

          {/* Solution 1: Own API Key */}
          <TabsContent value="own_key" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <Key size={32} className="text-[#2C4C3B] mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-[#2C4C3B] mb-2">Use Your Own API Key</h3>
                  <p className="text-sm text-gray-600">
                    Bring your own Emergent LLM API key. Your usage and billing will be managed directly through your account.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="api-key">Emergent LLM API Key</Label>
                  <Input
                    id="api-key"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-emergent-xxxxxxxxxxxxx"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Get your API key from <a href="https://emergentagent.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">emergentagent.com</a>
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveApiKey}
                    disabled={validatingKey || !apiKey.trim()}
                    className="bg-[#2C4C3B]"
                    data-testid="save-api-key-button"
                  >
                    {validatingKey ? 'Validating...' : 'Save & Validate Key'}
                  </Button>
                  {billingMethod === 'own_key' && (
                    <Badge variant="outline" className="ml-2 flex items-center gap-1">
                      <CheckCircle size={16} className="text-green-500" />
                      Active
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Solution 2: Purchase Credits */}
          <TabsContent value="credits" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-start gap-3 mb-6">
                <Coins size={32} className="text-[#2C4C3B] mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-[#2C4C3B] mb-2">Purchase AI Credits</h3>
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm text-gray-600">
                      Pay-as-you-go pricing. Each AI generation (chat or guide) costs 1 credit at <span className="font-semibold text-[#2C4C3B]" data-testid="credit-rate-display-tab">₹{CREDIT_PRICE_PER_UNIT}</span> per credit.
                    </p>
                    {isAdmin && !editingRate && (
                      <button type="button" data-testid="edit-credit-rate-btn-tab" onClick={() => { setRateInput(String(CREDIT_PRICE_PER_UNIT)); setEditingRate(true); }}
                        className="text-xs font-medium text-[#C05C42] border border-[#C05C42]/40 rounded px-2 py-1 hover:bg-[#C05C42] hover:text-white transition-colors">
                        Edit rate
                      </button>
                    )}
                    {isAdmin && editingRate && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#2C4C3B]">₹</span>
                        <Input type="number" min="0.01" step="0.01" value={rateInput} onChange={(e) => setRateInput(e.target.value)} data-testid="credit-rate-input-tab" className="w-28 h-8" />
                        <Button size="sm" data-testid="credit-rate-save-tab" onClick={saveCreditRate} className="bg-[#2C4C3B] text-white h-8">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingRate(false)} className="h-8">Cancel</Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {billingMethod !== 'credits' && (
                <Button
                  onClick={() => handleSaveBillingMethod('credits')}
                  variant="outline"
                  className="mb-6"
                >
                  Enable Credit-Based Billing
                </Button>
              )}

              {billingMethod === 'credits' && (
                <>
                  {/* Current Balance */}
                  <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600">Current Balance</div>
                        <div className="text-3xl font-bold text-[#2C4C3B]">
                          {creditsData?.credits_balance || 0}
                          <span className="text-lg font-normal text-gray-600 ml-2">credits</span>
                        </div>
                      </div>
                      <Lightning size={48} className="text-yellow-500" weight="fill" />
                    </div>
                  </Card>

                  {/* OLD: Credit Packages - REMOVED, now using flexible input above */}
                </>
              )}
            </Card>
          </TabsContent>
        </Tabs>
        </div>

        {/* Transaction History Table - Visible to both owner and admin */}
        {billingMethod === 'credits' && (
          <Card className="p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-[#2C4C3B] mb-1">Transaction History</h3>
                <p className="text-sm text-gray-600">Track your credit purchases and usage</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Current Balance</div>
                <div className="text-2xl font-bold text-[#2C4C3B]">
                  {creditsData?.credits_balance || 0} credits
                </div>
              </div>
            </div>

            {loadingTransactions ? (
              <div className="text-center py-8 text-gray-500">Loading transaction history...</div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No transactions yet. Purchase credits to get started!
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date & Time</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Credits Added</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Credits Used</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount Paid</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn, index) => (
                      <tr key={txn.transaction_id || index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(txn.timestamp).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          {txn.type === 'credit_purchase' ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">Purchase</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-200">Usage</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {txn.package_name}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-green-600">
                          {txn.credits_added > 0 ? `+${txn.credits_added}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-red-600">
                          {txn.credits_used > 0 ? `-${txn.credits_used}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-gray-700">
                          {txn.amount_paid > 0 ? `₹${txn.amount_paid.toFixed(2)}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-semibold text-[#2C4C3B]">
                          {txn.balance_after}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Info Cards - Admin only */}
        {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-4">
            <h4 className="font-semibold text-[#2C4C3B] mb-2 flex items-center gap-2">
              <Lightning size={20} />
              How It Works
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Each AI Guide generation = 1 credit</li>
              <li>• Each AI Chat message = 1 credit</li>
              <li>• Credits never expire</li>
              <li>• Top up anytime</li>
            </ul>
          </Card>

          <Card className="p-4">
            <h4 className="font-semibold text-[#2C4C3B] mb-2 flex items-center gap-2">
              <Key size={20} />
              API Key vs Credits
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Own Key:</strong> Direct billing to your account</li>
              <li>• <strong>Credits:</strong> Prepaid, no surprises</li>
              <li>• Switch anytime</li>
            </ul>
          </Card>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default BranchBillingSettings;
